import { Code, ConnectError } from '@connectrpc/connect';
import JSZip from 'jszip';
/** A save, restore or import made on a head that is no longer the head. */ export function isConcurrentEdit(err) {
    return ConnectError.from(err).code === Code.Aborted;
}
/** Tiptap document JSON for plain text: one paragraph per non-empty line. */ export function textToDoc(text) {
    const lines = text.replace(/\r\n?/g, '\n').split('\n').map((l)=>l.trim()).filter(Boolean);
    const doc = {
        type: 'doc',
        content: lines.map((line)=>({
                type: 'paragraph',
                content: [
                    {
                        type: 'text',
                        text: line
                    }
                ]
            }))
    };
    return {
        json: JSON.stringify(doc),
        text: lines.join('\n')
    };
}
const TEXT_EXTENSIONS = [
    '.txt',
    '.md',
    '.markdown',
    '.html',
    '.htm',
    '.docx'
];
/** Why a revised file cannot be read here, or '' when it can. */ export function unreadableFileReason(fileName) {
    const lower = fileName.toLowerCase();
    if (TEXT_EXTENSIONS.some((ext)=>lower.endsWith(ext))) return '';
    return `"${fileName}" can't be read here yet. Save it as .docx, .txt, .md or .html, or paste its text instead.`;
}
/** Plain text of a revised file: markup is dropped from .html, everything else is used as written. */ export function fileText(fileName, raw) {
    if (/\.html?$/i.test(fileName)) {
        const doc = new DOMParser().parseFromString(raw, 'text/html');
        doc.querySelectorAll('script,style').forEach((n)=>n.remove());
        const blocks = Array.from(doc.body.querySelectorAll('p,h1,h2,h3,h4,h5,h6,li,tr,div')).filter((n)=>!n.querySelector('p,h1,h2,h3,h4,h5,h6,li,tr,div')).map((n)=>(n.textContent ?? '').trim()).filter(Boolean);
        return blocks.length > 0 ? blocks.join('\n') : doc.body.textContent ?? '';
    }
    return raw;
}
const WORD_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
/** Plain text of a Word .docx: one line per paragraph, runs joined, tabs kept. */ export async function docxText(data) {
    const entry = (await JSZip.loadAsync(data)).file('word/document.xml');
    if (!entry) throw new Error('not a Word document');
    const xml = new DOMParser().parseFromString(await entry.async('string'), 'application/xml');
    const lines = [];
    for (const p of Array.from(xml.getElementsByTagNameNS(WORD_NS, 'p'))){
        let line = '';
        for (const n of Array.from(p.getElementsByTagNameNS(WORD_NS, '*'))){
            if (n.localName === 't') line += n.textContent ?? '';
            else if (n.localName === 'tab') line += '\t';
            else if (n.localName === 'br') line += ' ';
        }
        if (line.trim()) lines.push(line.trim());
    }
    return lines.join('\n');
}
/** Plain text of a revised file the author picked. */ export async function readRevisedFile(file) {
    return /\.docx$/i.test(file.name) ? docxText(await file.arrayBuffer()) : fileText(file.name, await file.text());
}
/**
 * The newest solicitation version (the second issuance onward: a change to the
 * source of record) issued after the section's head revision was written.
 */ export function sourceChangeSince(headCreatedAt, versions) {
    if (!headCreatedAt) return undefined;
    return versions.filter((v)=>v.versionNumber > 1 && new Date(v.issuedAt).getTime() > headCreatedAt.getTime()).sort((a, b)=>b.versionNumber - a.versionNumber)[0];
}
/** Milestone and review snapshots are locked; everything else is a working revision. */ export function isLocked(kind) {
    return kind === 'milestone' || kind === 'review';
}
export function provenanceLabel(row) {
    switch(row.provenance){
        case 'paste':
            return 'Pasted text';
        case 'external_file':
            return `Uploaded file ${row.provenanceFileName}`.trim();
        case 'import':
            return `Imported ${row.provenanceFileName}`.trim();
        default:
            return '';
    }
}
